import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import IconCircleInfo from "central-icons/IconCircleInfo";
import IconCrossSmall from "central-icons/IconCrossSmall";
import { useConvertToProjectDismissed } from "@/hooks/use-convert-to-project-dismissed";
import { useEnvironment, getRepoUrlFromInitializer } from "@/queries/environment-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useCreateProjectFromEnvironment, useListProjectsPaginated } from "@/queries/project-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { getRepoUrl, parseRepoUrl } from "@/repo-url";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useState, type FC } from "react";

const IconXWithSize = withGitpodSize(IconCrossSmall);

export const ConvertToProjectBanner: FC<{ environmentId: string }> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: organization } = useOrganization();
    const { isDismissed, isLoading: isDismissLoading, dismiss } = useConvertToProjectDismissed(environmentId);
    const { mutate: createProject, isPending: isCreating } = useCreateProjectFromEnvironment();
    const { toast } = useToast();
    const [isDismissedLocally, setIsDismissedLocally] = useState(false);
    const [hasErrored, setHasErrored] = useState(false);

    const isEligibleTier =
        organization?.tier === OrganizationTier.FREE_ONA || organization?.tier === OrganizationTier.CORE;
    const hasProject = !!environment?.metadata?.projectId;
    const repoInfo = environment ? getRepoUrl(environment) : undefined;
    const hasRepoUrl = !!repoInfo?.repoUrl;

    const shouldCheckExisting = !hasProject && hasRepoUrl && isEligibleTier;
    const { data: existingProjects, isLoading: isLoadingProjects } = useListProjectsPaginated({
        search: repoInfo?.repo,
        enabled: shouldCheckExisting,
    });

    const projectExistsForRepo =
        shouldCheckExisting &&
        existingProjects?.pages.some((page) =>
            page.projects.some((p) => {
                const rawUrl = getRepoUrlFromInitializer(p.initializer);
                const projectRepoUrl = rawUrl ? parseRepoUrl(rawUrl)?.repoUrl : undefined;
                return projectRepoUrl && projectRepoUrl === repoInfo?.repoUrl;
            }),
        );

    // Don't render while preference or projects are loading to avoid flash
    if (isDismissLoading || (shouldCheckExisting && isLoadingProjects)) {
        return null;
    }

    if (
        !environment ||
        !isEligibleTier ||
        hasProject ||
        !hasRepoUrl ||
        isDismissed ||
        isDismissedLocally ||
        projectExistsForRepo
    ) {
        return null;
    }

    const handleDismiss = () => {
        setIsDismissedLocally(true);
        dismiss();
    };

    const handleSaveAsProject = () => {
        const projectName = repoInfo?.repo || "my-project";
        createProject(
            { projectName, environmentId },
            {
                onSuccess: () => {
                    setHasErrored(false);
                    toast({
                        title: `Project "${projectName}" created`,
                        description: "Start from the project next time for faster startup times.",
                    });
                },
                onError: () => {
                    setHasErrored(true);
                },
            },
        );
    };

    return (
        <div
            className={cn(
                "relative w-full select-none border-b @container @md:flex-row",
                hasErrored
                    ? "border-border-destructive bg-surface-negative/10"
                    : "border-border-subtle bg-surface-brand-subtle",
            )}
            data-testid="convert-to-project-banner"
        >
            <div className="relative flex flex-col items-center justify-start gap-3 px-4 py-2 @md:flex-row @xl:justify-center">
                <span className={cn("flex flex-row items-center gap-2", { "w-full @xl:w-auto": hasErrored })}>
                    <IconCircleInfo
                        size={16}
                        className={cn("shrink-0", hasErrored ? "text-content-destructive" : "text-content-brand")}
                    />
                    <span
                        className={cn("text-sm", hasErrored ? "w-full text-content-destructive" : "text-content-brand")}
                    >
                        {hasErrored ? (
                            "Failed to create project. Please try again."
                        ) : (
                            <>
                                Create a{" "}
                                <a
                                    href="https://ona.com/docs/ona/projects/overview"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-content-brand/80"
                                >
                                    project
                                </a>{" "}
                                for this repo for one-click environments, faster startups, and shared config.
                            </>
                        )}
                    </span>
                </span>
                <div className="flex w-full flex-row justify-end gap-2 @md:w-auto">
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={handleDismiss}
                        disabled={isCreating}
                        className={cn(
                            "shrink-0 @md:hidden",
                            hasErrored
                                ? "border-content-destructive text-content-destructive hover:text-content-destructive"
                                : "border-content-brand text-content-brand hover:text-content-brand",
                        )}
                        data-tracking-id="convert-to-project-banner-dismiss-mobile"
                    >
                        Dismiss
                    </Button>
                    <Button
                        variant="outline"
                        className={cn(
                            hasErrored
                                ? "border-content-destructive text-content-destructive hover:text-content-destructive disabled:text-content-destructive/50"
                                : "border-content-brand text-content-brand hover:text-content-brand disabled:text-content-brand/50",
                        )}
                        size="xs"
                        onClick={handleSaveAsProject}
                        loading={isCreating}
                        disabled={isCreating}
                        data-tracking-id="convert-to-project-banner-save"
                    >
                        {hasErrored ? "Retry" : "Create"}
                    </Button>
                </div>
                <Button
                    data-testid="convert-to-project-dismiss"
                    data-tracking-id="convert-to-project-banner-dismiss"
                    aria-label="Dismiss"
                    variant="ghost"
                    size="xs"
                    className={cn(
                        "hidden shrink-0 rounded-full @md:inline-flex @xl:right-[18px] @2xl:absolute",
                        hasErrored ? "text-content-destructive" : "text-content-brand",
                    )}
                    LeadingIcon={IconXWithSize}
                    onClick={handleDismiss}
                />
            </div>
        </div>
    );
};
